import { useMemo } from "react";
import { X } from "lucide-react";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CompletionBanner } from "./CompletionBanner";
import { ScanPhaseSection } from "./ScanPhaseSection";
import { CostTicker } from "./CostTicker";
import type { ScanEventState, ScanFeedEvent } from "@/hooks/useScanEvents";

interface ScanProgressPanelProps {
  state: ScanEventState;
  onClose: () => void;
  onLocationClick?: (lat: number, lng: number) => void;
}

const PHASE_ORDER = ["keyword_scanning", "scanning", "screenshotting", "classifying", "website_classifying", "web_researching"];
const PHASE_LABELS: Record<string, string> = {
  keyword_scanning: "Keyword Search",
  scanning: "Nearby Search",
  screenshotting: "Screenshots",
  classifying: "Classification",
  website_classifying: "Website Analysis",
  web_researching: "Web Research",
};

const PHASE_EVENT_TYPES: Record<string, Set<string>> = {
  keyword_scanning: new Set(["keyword_location_found"]),
  scanning: new Set(["location_found"]),
  screenshotting: new Set(["screenshot_complete"]),
  classifying: new Set(["classification_complete"]),
  website_classifying: new Set(["website_classify_complete"]),
  web_researching: new Set(["web_research_complete"]),
};

function getPhaseStatus(phase: string, currentPhase: string | null, isComplete: boolean): "pending" | "active" | "completed" {
  if (isComplete) return "completed";
  if (!currentPhase) return "pending";
  const currentIdx = PHASE_ORDER.indexOf(currentPhase);
  const phaseIdx = PHASE_ORDER.indexOf(phase);
  if (phaseIdx < currentIdx) return "completed";
  if (phaseIdx === currentIdx) return "active";
  return "pending";
}

export function ScanProgressPanel({ state, onClose, onLocationClick }: ScanProgressPanelProps) {
  const phaseEvents = useMemo(() => {
    const result: Record<string, ScanFeedEvent[]> = {};
    for (const phase of PHASE_ORDER) {
      const types = PHASE_EVENT_TYPES[phase]!;
      result[phase] = state.events.filter((e) => types.has(e.event.type));
    }
    return result;
  }, [state.events]);

  const openPhases = useMemo(() => {
    const phases: string[] = [];
    for (const phase of PHASE_ORDER) {
      const status = getPhaseStatus(phase, state.currentPhase, state.isComplete);
      if (status === "active") phases.push(phase);
    }
    // If complete, open the last phase
    if (state.isComplete) phases.push(PHASE_ORDER[PHASE_ORDER.length - 1]!);
    return phases;
  }, [state.currentPhase, state.isComplete]);

  const totalCost =
    (state.keywordStats?.estimatedCost ?? 0) +
    (state.scanStats?.estimatedCost ?? 0) +
    (state.result?.totalCost ?? 0);

  const phaseSummaries: Record<string, string> = {
    keyword_scanning: state.keywordStats
      ? `+${state.keywordLocationsFound} new locations`
      : "",
    scanning: state.scanStats
      ? `${state.scanStats.locationsFound} locations`
      : "",
    screenshotting: state.screenshotStats
      ? `${state.screenshotStats.completed} done${state.screenshotStats.failed > 0 ? `, ${state.screenshotStats.failed} failed` : ""}`
      : "",
    classifying: state.classificationStats
      ? `${state.classificationStats.sportFieldsFound} sport fields`
      : "",
    website_classifying: state.websiteStats
      ? `${state.websiteStats.completed} classified`
      : "",
    web_researching: state.webResearchStats
      ? `${state.webResearchStats.completed} researched`
      : "",
  };

  return (
    <div className="absolute top-0 right-0 z-20 h-full w-[420px] bg-card border-l shadow-xl flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h2 className="text-sm font-semibold">Scan Progress</h2>
        <Button variant="ghost" size="icon" className="size-7" onClick={onClose}>
          <X className="size-4" />
        </Button>
      </div>

      {/* Completion banner */}
      {state.isComplete && state.result && <CompletionBanner result={state.result} />}

      {/* Error banner */}
      {state.error && (
        <div className="mx-3 mt-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-400">
          {state.error}
        </div>
      )}

      {/* Phase sections */}
      <ScrollArea className="flex-1 min-h-0">
        <Accordion type="multiple" defaultValue={openPhases} value={openPhases}>
          {PHASE_ORDER.map((phase) => {
            const status = getPhaseStatus(phase, state.currentPhase, state.isComplete);
            let current = 0;
            let total = 0;

            if (phase === "keyword_scanning") {
              current = state.keywordLocationsFound;
              // Keyword scanning doesn't have a fixed total
              total = 0;
            } else if (phase === "scanning") {
              current = state.scanStats?.locationsFound ?? 0;
              // Scanning doesn't have a fixed total, use locationsFound as both
              total = 0;
            } else if (phase === "screenshotting") {
              current = state.screenshotStats?.completed ?? 0;
              total = state.screenshotStats?.total ?? 0;
            } else if (phase === "classifying") {
              current = state.classificationStats?.completed ?? 0;
              total = state.classificationStats?.total ?? 0;
            } else if (phase === "website_classifying") {
              current = state.websiteStats?.completed ?? 0;
              total = state.websiteStats?.total ?? 0;
            } else if (phase === "web_researching") {
              current = state.webResearchStats?.completed ?? 0;
              total = state.webResearchStats?.total ?? 0;
            }

            return (
              <ScanPhaseSection
                key={phase}
                value={phase}
                title={PHASE_LABELS[phase]!}
                status={status}
                current={current}
                total={total}
                summary={phaseSummaries[phase]}
                events={phaseEvents[phase] ?? []}
                onLocationClick={onLocationClick}
              />
            );
          })}
        </Accordion>
      </ScrollArea>

      {/* Cost footer */}
      <CostTicker cost={totalCost} />
    </div>
  );
}
